import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { callWithRetry } from './callableRetry';
import type {
  GetStartupDashboardActivityResponse,
  StartupActivityTimelineItem,
  StartupInterestedInvestorItem,
} from '../types/startupDashboard';

type UseStartupDashboardActivityResult = {
  interestedInvestors: StartupInterestedInvestorItem[];
  activityTimeline: StartupActivityTimelineItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

export function useStartupDashboardActivity(): UseStartupDashboardActivityResult {
  const { user, firebaseUser } = useAuth();
  const [interestedInvestors, setInterestedInvestors] = useState<StartupInterestedInvestorItem[]>(
    []
  );
  const [activityTimeline, setActivityTimeline] = useState<StartupActivityTimelineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canFetch = useMemo(() => {
    return Boolean(firebaseUser && user?.userType === 'startup');
  }, [firebaseUser, user?.userType]);

  const refetch = useCallback(async () => {
    if (!canFetch) {
      setInterestedInvestors([]);
      setActivityTimeline([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const callable = httpsCallable<Record<string, never>, GetStartupDashboardActivityResponse>(
        functions,
        'getStartupDashboardActivity'
      );
      const result = await callWithRetry(() => callable({}));
      const data = result.data;

      if (!data.success) {
        setInterestedInvestors([]);
        setActivityTimeline([]);
        setError(data.error || 'Failed to load startup dashboard activity');
        return;
      }

      setInterestedInvestors(
        Array.isArray(data.interestedInvestors) ? data.interestedInvestors : []
      );
      setActivityTimeline(Array.isArray(data.activityTimeline) ? data.activityTimeline : []);
    } catch (err: any) {
      setInterestedInvestors([]);
      setActivityTimeline([]);
      setError(err?.message || 'Failed to load startup dashboard activity');
    } finally {
      setLoading(false);
    }
  }, [canFetch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    interestedInvestors,
    activityTimeline,
    loading,
    error,
    refetch,
  };
}
